import type { ScreenTourMap, ScreenTourStep } from './types';

/**
 * Administrar › Usuarios, Sedes (crear y editar), Representantes, Clientes y Rutas.
 * La lista de Sedes es un piloto y vive en `offices.ts`.
 */

const userFormSteps = (mode: 'new' | 'edit'): ScreenTourStep[] => [
  { text: mode === 'new' ? 'tours.userForm.welcomeNew' : 'tours.userForm.welcomeEdit' },
  { text: 'tours.userForm.person', target: 'user-form-person' },
  { text: 'tours.userForm.access', target: 'user-form-access' },
  { text: 'tours.userForm.role', target: 'user-form-role' },
  mode === 'new'
    ? { text: 'tours.userForm.password', target: 'user-form-password' }
    : { text: 'tours.userForm.changePassword', target: 'user-form-change-password' },
  { text: 'tours.userForm.offices', target: 'user-form-offices' },
  { text: mode === 'new' ? 'tours.userForm.saveNew' : 'tours.userForm.saveEdit', target: 'save' },
];

const officeFormSteps = (mode: 'new' | 'edit'): ScreenTourStep[] => [
  { text: mode === 'new' ? 'tours.officeForm.welcomeNew' : 'tours.officeForm.welcomeEdit' },
  { text: 'tours.officeForm.data', target: 'office-form-data' },
  { text: mode === 'new' ? 'tours.officeForm.type' : 'tours.officeForm.typeEdit', target: 'office-form-type' },
  { text: 'tours.officeForm.clients', target: 'office-form-clients' },
  { text: mode === 'new' ? 'tours.officeForm.saveNew' : 'tours.officeForm.saveEdit', target: 'save' },
];

const routeFormSteps = (mode: 'new' | 'edit'): ScreenTourStep[] => [
  { text: mode === 'new' ? 'tours.routeForm.welcomeNew' : 'tours.routeForm.welcomeEdit' },
  ...(mode === 'new' ? [{ text: 'tours.routeForm.inherited', target: 'route-new-inherited' } as ScreenTourStep] : []),
  { text: mode === 'new' ? 'tours.routeForm.data' : 'tours.routeForm.dataEdit', target: 'route-form-data' },
  { text: mode === 'new' ? 'tours.routeForm.salesRep' : 'tours.routeForm.salesRepEdit', target: 'route-form-sales-rep' },
  { text: 'tours.routeForm.loans', target: 'route-form-loans' },
  { text: 'tours.routeForm.blockLoan', target: 'route-block-loan-installments' },
  { text: 'tours.routeForm.fee', target: 'route-form-fee' },
  { text: 'tours.routeForm.limits', target: 'route-form-limits' },
  { text: mode === 'new' ? 'tours.routeForm.saveNew' : 'tours.routeForm.saveEdit', target: 'save' },
];

export const adminATours: ScreenTourMap = {
  // ── Usuarios ────────────────────────────────────────────────────────────────
  '/admin/users': {
    key: 'screen-users',
    steps: [
      { text: 'tours.users.welcome' },
      { text: 'tours.users.new', target: 'users-new' },
      { text: 'tours.users.office', target: 'users-office' },
      { text: 'tours.users.route', target: 'users-route' },
      { text: 'tours.users.search', target: 'users-search' },
      { text: 'tours.users.export', target: 'users-export' },
      { text: 'tours.users.table', target: 'users' },
      { text: 'tours.users.state', target: 'users-col-state', whenMissing: 'center' },
      { text: 'tours.users.actions', target: 'users-actions', whenMissing: 'center' },
      { text: 'tours.users.pagination', target: 'users-pagination' },
    ],
  },
  '/admin/users/new': { key: 'screen-user-new', steps: userFormSteps('new') },
  '/admin/users/:id/edit': { key: 'screen-user-edit', steps: userFormSteps('edit') },
  '/admin/users/:id/offices': {
    key: 'screen-user-offices',
    steps: [
      { text: 'tours.userOffices.welcome' },
      { text: 'tours.userOffices.list', target: 'user-offices-list' },
      { text: 'tours.userOffices.all', target: 'user-offices-all', whenMissing: 'center' },
      { text: 'tours.userOffices.save', target: 'save' },
    ],
  },
  '/admin/users/:id/routes': {
    key: 'screen-user-routes',
    steps: [
      { text: 'tours.userRoutes.welcome' },
      { text: 'tours.userRoutes.office', target: 'user-routes-office' },
      { text: 'tours.userRoutes.list', target: 'user-routes-list' },
      { text: 'tours.userRoutes.all', target: 'user-routes-all', whenMissing: 'center' },
      { text: 'tours.userRoutes.save', target: 'save' },
    ],
  },

  // ── Sedes: crear y editar (la lista es el piloto de offices.ts) ─────────────
  '/admin/offices/new': { key: 'screen-office-new', steps: officeFormSteps('new') },
  '/admin/offices/:id/edit': { key: 'screen-office-edit', steps: officeFormSteps('edit') },

  // ── Representantes ──────────────────────────────────────────────────────────
  '/admin/sales-reps': {
    key: 'screen-sales-reps',
    steps: [
      { text: 'tours.salesReps.welcome' },
      { text: 'tours.salesReps.new', target: 'sales-reps-new' },
      { text: 'tours.salesReps.office', target: 'sales-reps-office' },
      { text: 'tours.salesReps.route', target: 'sales-reps-route' },
      { text: 'tours.salesReps.unassigned', target: 'sales-reps-unassigned' },
      { text: 'tours.salesReps.search', target: 'sales-reps-search' },
      { text: 'tours.salesReps.export', target: 'sales-reps-export' },
      { text: 'tours.salesReps.table', target: 'sales-reps' },
      { text: 'tours.salesReps.officeCol', target: 'sales-reps-col-office', whenMissing: 'center' },
      { text: 'tours.salesReps.session', target: 'sales-reps-col-session', whenMissing: 'center' },
      { text: 'tours.salesReps.actions', target: 'sales-reps-actions', whenMissing: 'center' },
      { text: 'tours.salesReps.pagination', target: 'sales-reps-pagination' },
    ],
  },
  '/admin/sales-reps/new': {
    key: 'screen-sales-rep-new',
    steps: [
      { text: 'tours.salesRepForm.welcomeNew' },
      { text: 'tours.salesRepForm.person', target: 'sales-rep-form-person' },
      { text: 'tours.salesRepForm.idNumber', target: 'sales-rep-form-id-number' },
      { text: 'tours.salesRepForm.contact', target: 'sales-rep-form-contact' },
      { text: 'tours.salesRepForm.access', target: 'sales-rep-form-access' },
      { text: 'tours.salesRepForm.saveNew', target: 'save' },
    ],
  },
  '/admin/sales-reps/:id/edit': {
    key: 'screen-sales-rep-edit',
    steps: [
      { text: 'tours.salesRepForm.welcomeEdit' },
      { text: 'tours.salesRepForm.personEdit', target: 'sales-rep-form-person' },
      { text: 'tours.salesRepForm.contact', target: 'sales-rep-form-contact' },
      { text: 'tours.salesRepForm.accessEdit', target: 'sales-rep-form-access' },
      { text: 'tours.salesRepForm.saveEdit', target: 'save' },
      { text: 'tours.salesRepForm.devices', target: 'sales-rep-form-devices' },
    ],
  },
  '/admin/sales-reps/:id/offices': {
    key: 'screen-sales-rep-offices',
    steps: [
      { text: 'tours.salesRepOffices.welcome' },
      { text: 'tours.salesRepOffices.list', target: 'sales-rep-offices-list' },
      { text: 'tours.salesRepOffices.all', target: 'sales-rep-offices-all', whenMissing: 'center' },
      { text: 'tours.salesRepOffices.save', target: 'save' },
    ],
  },
  '/admin/sales-reps/:id/routes': {
    key: 'screen-sales-rep-routes',
    steps: [
      { text: 'tours.salesRepRoutes.welcome' },
      { text: 'tours.salesRepRoutes.office', target: 'sales-rep-routes-office' },
      { text: 'tours.salesRepRoutes.list', target: 'sales-rep-routes-list' },
      { text: 'tours.salesRepRoutes.save', target: 'save' },
    ],
  },

  // ── Clientes ────────────────────────────────────────────────────────────────
  '/admin/clients': {
    key: 'screen-clients',
    steps: [
      { text: 'tours.clients.welcome' },
      { text: 'tours.clients.office', target: 'clients-office' },
      { text: 'tours.clients.route', target: 'clients-route' },
      { text: 'tours.clients.new', target: 'client-new' },
      { text: 'tours.clients.trash', target: 'clients-trash' },
      { text: 'tours.clients.search', target: 'clients-search' },
      { text: 'tours.clients.export', target: 'clients-export' },
      { text: 'tours.clients.table', target: 'clients' },
      { text: 'tours.clients.select', target: 'clients-col-fullName', whenMissing: 'center' },
      { text: 'tours.clients.creditLimit', target: 'clients-col-creditLimit', whenMissing: 'center' },
      { text: 'tours.clients.actions', target: 'clients-actions', whenMissing: 'center' },
      { text: 'tours.clients.pagination', target: 'clients-pagination' },
    ],
  },
  '/admin/clients/new': {
    key: 'screen-client-new',
    steps: [
      { text: 'tours.clientForm.welcomeNew' },
      { text: 'tours.clientForm.office', target: 'client-form-office' },
      { text: 'tours.clientForm.data', target: 'client-form-data' },
      { text: 'tours.clientForm.idNumber', target: 'client-form-id-number' },
      { text: 'tours.clientForm.address', target: 'client-form-address' },
      { text: 'tours.clientForm.location', target: 'client-form-location' },
      { text: 'tours.clientForm.cosigner', target: 'client-form-cosigner' },
      { text: 'tours.clientForm.saveNew', target: 'save' },
    ],
  },
  '/admin/clients/:id/edit': {
    key: 'screen-client-edit',
    steps: [
      { text: 'tours.clientForm.welcomeEdit' },
      { text: 'tours.clientForm.officeEdit', target: 'client-form-office' },
      { text: 'tours.clientForm.dataEdit', target: 'client-form-data' },
      { text: 'tours.clientForm.address', target: 'client-form-address' },
      { text: 'tours.clientForm.locationEdit', target: 'client-form-location' },
      { text: 'tours.clientForm.cosignerEdit', target: 'client-form-cosigner' },
      { text: 'tours.clientForm.saveEdit', target: 'save' },
    ],
  },

  // ── Rutas ───────────────────────────────────────────────────────────────────
  '/admin/routes': {
    key: 'screen-routes',
    steps: [
      { text: 'tours.routes.welcome' },
      { text: 'tours.routes.new', target: 'routes-new' },
      { text: 'tours.routes.office', target: 'routes-office' },
      { text: 'tours.routes.search', target: 'routes-search' },
      { text: 'tours.routes.export', target: 'routes-export' },
      { text: 'tours.routes.table', target: 'routes' },
      { text: 'tours.routes.responsible', target: 'routes-col-responsible', whenMissing: 'center' },
      { text: 'tours.routes.actions', target: 'routes-actions', whenMissing: 'center' },
      { text: 'tours.routes.pagination', target: 'routes-pagination' },
    ],
  },
  '/admin/routes/new': { key: 'screen-route-new', steps: routeFormSteps('new') },
  '/admin/routes/:id/edit': { key: 'screen-route-edit', steps: routeFormSteps('edit') },
};
